import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { AddonsType, MealType } from 'utils/types';
import PageText from 'components/base/page-text';
import { toast } from 'react-toastify';
import { ToastMessage, Button, Breadcrumbs } from '@cokitchen/cokitchen-components';
import OrganizeItem from 'components/base/organize-item';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import AddonCategory from './addon-category';
import { ROUTE_KEYS } from 'utils/constants';

const MealAddonCategories = () => {
  const { id } = useParams();
  const [addonsList, setAddonsList] = useState<AddonsType[]>([]);
  const [prevAddonList, setPrevAddonsList] = useState<AddonsType[]>([]);
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [mealData, setMealData] = useState<MealType | null>(null);
  const [step, setStep] = useState(0);
  const [dragAndDropActive, setDragAndDropActive] = useState(false);
  const { brand, meal_category } = mealData || {};
  const breadcrumbs = [
    {
      name: brand ? `View ${brand.name}` : '...',
      path: `${ROUTE_KEYS.MEALS}?brand_id=${brand?.id}`
    },
    {
      name: mealData?.name || '...',
      path: `${ROUTE_KEYS.MEALS}/${meal_category?.id}`
    },
    {
      name: 'Addon category',
      path: ''
    }
  ];

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over, collisions } = event;

    if (!collisions?.length) return;
    if (active?.id === over?.id) return;

    setAddonsList((addons = []) => {
      const oldIndex = addons?.findIndex((addon) => addon.id === active.id);
      const newIndex = addons?.findIndex((addon) => addon.id === over?.id);
      return arrayMove(addons, oldIndex, newIndex);
    });
    setStep((prev) => prev + 1);
  };

  const updatePosition = async (items: AddonsType[]) => {
    setUpdateLoading(true);
    const addon_category_ids = items.map((item) => item?.id);
    const payload = {
      meal_categories: addon_category_ids,
      brand_id: items[0]?.addons[0]?.meal_data?.brand?.id,
      is_addon: true,
      meal_id: items[0]?.meal_parent_id
    };
    try {
      const res = await apiService.put(`marketing/meal-category/position`, { data: payload });
      if (res) {
        setStep(0);
        cancelUpdate();
      }
    } catch (error: any) {
      setAddonsList(prevAddonList);
      toast(<ToastMessage message={error.response.data.message} type='error' />);
    } finally {
      setUpdateLoading(false);
    }
  };

  const fetchMealData = async () => {
    try {
      const response = await apiService.get(`meal/${id}`);
      const { data } = response.data;
      setMealData(data);
    } catch (error: any) {
      console.log({ error });
    }
  };

  const cancelUpdate = () => {
    setAddonsList(prevAddonList);
    setStep(0);
    setDragAndDropActive(false);
  };

  const fetchData = async (reOrder?: boolean) => {
    if (!reOrder) setLoading(true);
    try {
      const response = await apiService.post(`internal/meal-addons`, {
        data: {
          meal_id: id,
          by_category: true
        },
        isInternal: true
      });
      const { data } = response.data;
      setAddonsList(data?.addons);
      setPrevAddonsList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMealData();
    fetchData();
  }, []);

  useEffect(() => {
    if (step === 0) setPrevAddonsList(JSON.parse(JSON.stringify(addonsList)));
  }, [step]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );
  return (
    <div>
      <div className='flex flex-wrap items-start justify-between mb-4'>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {!dragAndDropActive ? (
          <OrganizeItem text='addon categories' onClick={() => setDragAndDropActive(true)} />
        ) : (
          <div className='gap-3 flex items-center'>
            <Button
              small
              className='flex justify-end h-10 w-[100px] ml-auto'
              variant='outline'
              color='primary'
              disabled={updateLoading}
              onClick={cancelUpdate}>
              Cancel
            </Button>

            <Button
              small
              className='flex justify-end h-10 w-[100px] ml-auto'
              variant='block'
              color='primary'
              loading={updateLoading}
              disabled={
                updateLoading || JSON.stringify(addonsList) === JSON.stringify(prevAddonList)
              }
              onClick={() => updatePosition(addonsList ?? [])}>
              Save
            </Button>
          </div>
        )}
      </div>
      {loading ? (
        <PageText text='Loading...' />
      ) : addonsList?.length === 0 ? (
        <PageText text='No addons found' />
      ) : (
        <div className='w-full flex flex-col gap-[24px]'>
          {dragAndDropActive ? (
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} sensors={sensors}>
              <SortableContext items={addonsList} strategy={verticalListSortingStrategy}>
                {addonsList?.map((addon) => (
                  <AddonCategory
                    key={addon.id}
                    item={addon}
                    itemList={addonsList}
                    onRefresh={fetchData}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            addonsList?.map((addon) => (
              <AddonCategory
                key={addon.id}
                item={addon}
                itemList={addonsList}
                onRefresh={fetchData}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MealAddonCategories;
